import React, { Fragment, useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  CircularProgress,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCertificate,
  faBullhorn,
  faFileCertificate,
  faSuitcase,
  faArrowTrendUp,
  faStars,
  faCoins,
  faList,
  faListDropdown,
  faUserGroup,
} from "@fortawesome/pro-duotone-svg-icons";
import {
  faCheckCircle,
  faCircle,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import ErrorBoundary from "../../../ErrorBoundry";
import { advanceSearch } from "../../../_helper/services/advanceSearchv2";
import { getUserRole, ShowError, ShowSucess } from "../../../_helper/helper";
import { put } from "../../../_helper/api_helper";
import { getFormalApiErrorMessage } from "../../../utils/apiError";
import { useAuth } from "oidc-react";

const renderValue = (value) =>
  value !== null && value !== undefined ? value : "N/A";

// Shared sizing (~1.5x the MUI "small" default) for the primary action
// buttons/chips on each certification card (Approve Certification,
// Certification Approved, View Details). Any new action button added to
// this card should reuse these so it stays visually consistent with the
// others rather than needing its own one-off sizing.
const CARD_ACTION_BUTTON_SX = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  borderRadius: "20px",
  fontSize: "1.1rem",
};
const CARD_ACTION_CHIP_SX = {
  fontSize: "1.1rem",
  height: "36px",
  "& .MuiChip-label": { px: 2 },
};
const CARD_ACTION_ICON_SIZE = "lg";
const CARD_ACTION_BUTTON_SIZE = "large";

function AllCertificationsList() {
  const { userData } = useAuth();
  const Role = getUserRole(userData);
  const baseURL = process.env.REACT_APP_API_DOTNET_URL;
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvalRequest, setApprovalRequest] = useState(null);
  // Per-CERTIFICATION_PID: did every applicant already get a final StatusCode
  // and none of them is "SEL" (Selected)? Same rule ViewCertification.jsx /
  // CertificatePrepare.jsx use for their "No Applicant Selected" warning.
  const [allNotSelectedByCert, setAllNotSelectedByCert] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = {
        tableName: "CERT-LIST",
        searchParams: "[]",
        columns: "*",
        location: "pmis-global",
        pageNumber: 1,
        pageSize: 100,
        sortData: " created_on desc",
        additionalWhere: "",
        qID: 0,
      };
      const response = await advanceSearch(payload);
      setData(response?.results || []);
    } catch (error) {
      ShowError("Failed to load certifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchApplicantSelectionStatus = async (item) => {
    const params = [
      {
        columnName: "ANNOUNCEMENT_ID",
        value: item.ANNOUNCEMENT_ID,
        operator: "=",
        dataType: "varchar(MAX)",
        logicOperator: "AND",
      },
      {
        columnName: "CERTIFICATION_PID",
        value: item.CERTIFICATION_PID,
        operator: "=",
        dataType: "varchar(MAX)",
        logicOperator: "AND",
      },
    ];
    const payload = {
      tableName: "GET-CERTIFICATION-APP",
      searchParams: JSON.stringify(params),
      columns: "*",
      location: "pmis-global",
      pageNumber: 1,
      pageSize: 9999,
      sortData: "",
      additionalWhere: "",
      qID: 0,
    };
    try {
      const response = await advanceSearch(payload);
      const applicants = response.results || [];
      const allNotSelected =
        applicants.length > 0 &&
        applicants.every((a) => Boolean(a.StatusCode)) &&
        applicants.every((a) => a.StatusCode !== "SEL");
      setAllNotSelectedByCert((prev) => ({
        ...prev,
        [item.CERTIFICATION_PID]: allNotSelected,
      }));
    } catch (error) {
      console.error("Error checking applicant selection status:", error);
    }
  };

  // Loads each cert's applicant-selection status so the "No Applicant Selected"
  // warning can be shown without needing a separate step page.
  useEffect(() => {
    data.forEach((item) => {
      if (item.CERTIFICATION_PID && !(item.CERTIFICATION_PID in allNotSelectedByCert)) {
        fetchApplicantSelectionStatus(item);
      }
    });
  }, [data]);

  const handleView = (item) => {
    const annId = item?.ANNOUNCEMENT_ID?.trim() ?? "";
    const certPid = item?.CERTIFICATION_PID?.trim() ?? "";
    navigate(`/certificate-tracking/${annId}/${certPid}`);
  };

  const handleDOADirectorApprovelRequest = async (certificationPid, announcementId) => {
    const formData = {
      certificatioN_PID: certificationPid,
      announcemenT_ID: announcementId,
      doA_Director_Approvel_request: true
    };

    setLoading(true);
    try {
      const response = await put(`${baseURL}/Certification/DOADirectorApprovel`, formData);
      if (response) {
        ShowSucess('Approved successfully');
        await fetchData();
      } else {
        ShowError('Return failed');
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      ShowError(
        getFormalApiErrorMessage(
          error,
          "We are unable to process the approval request at this time. Please try again shortly."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalConfirm = () => {
    if (!approvalRequest) return;

    const { certificationPid, announcementId } = approvalRequest;
    setApprovalRequest(null);
    handleDOADirectorApprovelRequest(certificationPid, announcementId);
  };

  return (
    <ErrorBoundary>
      <Fragment>
        <Container fluid={true}>
          <Row className="bg-primary rounded py-2 mb-3 sticky-top">
            <Col className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faCertificate} size="xl" color="white" />
              <Typography variant="h6" style={{ color: "white" }}>
                All Certifications List
              </Typography>
            </Col>
          </Row>

          {loading ? (
            <Row>
              {[1, 2, 3].map((_, i) => (
                <Col xs="12" key={i} className="p-2">
                  <Card>
                    <CardHeader title={<Skeleton variant="text" width="60%" />} />
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Skeleton variant="text" width="40%" />
                        </Grid>
                        <Grid item xs={6}>
                          <Skeleton variant="text" width="80%" />
                          <Skeleton variant="text" width="80%" />
                        </Grid>
                        <Grid item xs={6}>
                          <Skeleton variant="text" width="80%" />
                          <Skeleton variant="text" width="80%" />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Row>
              {data.map((item, index) => (
                <Col xs="12" key={index} className="p-2">
                  <Card>
                    <CardHeader
                      title={
                        <div>
                          <div className="d-flex gap-4 align-items-center justify-content-center flex-wrap">
                            {[
                              { label: "ASO Process", done: !!(item.DOA_Director_Approvel_request) },
                              { label: "AD Approval", done: !!item.Agency_Director },
                              { label: "POC Approval", done: !!item.Point_of_Contact },
                              { label: "EEO & Personnel Approval", done: !!(item.EEO_Representative && item.Personal_officer) },
                              { label: "Send Certificate", done: item.Send_Certificate_Packet === true },
                              { label: "Interview Process", done: item.ContactedExistsFeedback === 1 },
                              // { label: "Interview Result", done: item.Return_To_HR === true },
                              { label: "Submit GG1", done: item.Return_To_HR === true },
                            ].map(({ label, done }) => (
                              <Typography key={label} variant="body2" className="d-flex align-items-center gap-2">
                                <b>{label}:</b>
                                <FontAwesomeIcon color={done ? "green" : "lightgrey"} icon={done ? faCheckCircle : faCircle} size="sm" />
                              </Typography>
                            ))}
                          </div>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="d-flex gap-2 align-items-center">
                                <Typography variant="h6">
                                  <FontAwesomeIcon
                                    color="var(--theme-default)"
                                    icon={faFileCertificate}
                                    size="lg"
                                  />{" "}
                                  Certification ID:{" "}
                                  <u>{renderValue(item.CERTIFICATION_PID)}</u>
                                </Typography>
                              </div>
                              <p className="d-flex align-items-center gap-2 mb-0">
                                <FontAwesomeIcon
                                  color="var(--theme-default)"
                                  icon={faBullhorn}
                                  size="md"
                                />{" "}
                                {renderValue(item.ANNOUNCEMENT_ID)}
                                <Chip
                                  size="small"
                                  label="Open"
                                  sx={{ color: "white", background: "lightgreen" }}
                                />
                              </p>
                            </div>
                            <div className="d-flex flex-column gap-2 align-items-end">
                              {Role === "DOA-DIRECTOR" && (
                                <div>
                                  {!item?.DOA_Director_Approvel_request ? (
                                    <Button
                                      color="primary"
                                      size={CARD_ACTION_BUTTON_SIZE}
                                      sx={CARD_ACTION_BUTTON_SX}
                                      variant="contained"
                                      onClick={() =>
                                        setApprovalRequest({
                                          certificationPid: item?.CERTIFICATION_PID?.trim(),
                                          announcementId: item?.ANNOUNCEMENT_ID?.trim(),
                                        })
                                      }
                                    >
                                      <FontAwesomeIcon
                                        color="white"
                                        icon={faCheckCircle}
                                        size={CARD_ACTION_ICON_SIZE}
                                        style={{ cursor: "pointer" }}
                                      />
                                      Approve Certification
                                    </Button>
                                  ) : (
                                    <Chip
                                      label="Certification Approved"
                                      color="warning"
                                      sx={CARD_ACTION_CHIP_SX}
                                    />
                                  )}
                                </div>
                              )}
                              <div className="d-flex gap-3 align-items-center">
                                <Typography variant="body2">
                                  <FontAwesomeIcon
                                    color="var(--theme-default)"
                                    icon={faUserGroup}
                                    size="sm"
                                  />{" "}
                                  Applicants:{" "}
                                  <Chip
                                    size="small"
                                    sx={{ color: "white", background: "var(--theme-default)" }}
                                    label={renderValue(item.TotalApp ?? 0)}
                                  />
                                </Typography>
                                {item?.GenFromCreated != null && (
                                  <Typography variant="body2">
                                    GA Elapsed:{" "}
                                    <Chip
                                      size="small"
                                      label={`${item.GenFromCreated} days`}
                                      variant="outlined"
                                    />
                                  </Typography>
                                )}
                                {item?.CertFromCreated != null && (
                                  <Typography variant="body2">
                                    Cert Elapsed:{" "}
                                    <Chip
                                      size="small"
                                      label={`${item.CertFromCreated} days`}
                                      variant="outlined"
                                    />
                                  </Typography>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      }
                    />
                    <CardContent>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faSuitcase}
                              size="md"
                            />{" "}
                            Position: {renderValue(item.POSITION_NO)} -{" "}
                            {renderValue(item.PositionTitle)}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} className="d-flex align-items-center gap-3">
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faArrowTrendUp}
                              size="sm"
                            />{" "}
                            Step: {renderValue(item.StepRange)}
                          </Typography>
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faStars}
                              size="sm"
                            />{" "}
                            Grade: {renderValue(item.Grade)}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} className="d-flex align-items-center gap-3">
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faCoins}
                              size="sm"
                            />{" "}
                            Rate: ${renderValue(item.HourlyRateRange)}
                          </Typography>
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faDollarSign}
                              size="sm"
                            />{" "}
                            Salary: ${renderValue(item.SalaryRange)}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} />

                        <Grid item xs={4}>
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faListDropdown}
                              size="sm"
                            />{" "}
                            Eligibility IDs: {renderValue(item.ELIGIBILITY_IDS)}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body1">
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faList}
                              size="sm"
                            />{" "}
                            GA ID: {renderValue(item.GENERAL_AVAILIBILITY_ID)}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} className="d-flex justify-content-end">
                          <Button
                            variant="outlined"
                            size={CARD_ACTION_BUTTON_SIZE}
                            sx={CARD_ACTION_BUTTON_SX}
                            onClick={() => handleView(item)}
                          >
                            <FontAwesomeIcon
                              color="var(--theme-default)"
                              icon={faFileCertificate}
                              size={CARD_ACTION_ICON_SIZE}
                            />
                            View Details
                          </Button>
                        </Grid>

                        <Grid container item xs={12} alignItems="center">
                          {allNotSelectedByCert[item.CERTIFICATION_PID] && (
                            <Grid item xs={6}>
                              <Typography
                                component="span"
                                sx={{ color: "error.main", fontWeight: "bold" }}
                              >
                                No Applicant Selected. Re-Certification is required.
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={allNotSelectedByCert[item.CERTIFICATION_PID] ? 6 : 12}>
                            <div className="d-flex gap-4 align-items-center justify-content-end">
                              {/* Approve Certification / Certification Approved moved to the card header, next to Applicants. */}
                              <Typography variant="body2" className="d-flex align-items-center gap-2">
                                <b>Point of Contact:</b>
                                <FontAwesomeIcon color={item.Point_of_Contact ? "green" : "lightgrey"} icon={item.Point_of_Contact ? faCheckCircle : faCircle} size="sm" />
                              </Typography>
                              <Typography variant="body2" className="d-flex align-items-center gap-2">
                                <b>EEO Representative:</b>
                                <FontAwesomeIcon color={item.EEO_Representative ? "green" : "lightgrey"} icon={item.EEO_Representative ? faCheckCircle : faCircle} size="sm" />
                              </Typography>
                              <Typography variant="body2" className="d-flex align-items-center gap-2">
                                <b>Personnel Officer:</b>
                                <FontAwesomeIcon color={item.Personal_officer ? "green" : "lightgrey"} icon={item.Personal_officer ? faCheckCircle : faCircle} size="sm" />
                              </Typography>
                              <Typography variant="body2" className="d-flex align-items-center gap-2">
                                <b>Agency Director:</b>
                                <FontAwesomeIcon color={item.Agency_Director ? "green" : "lightgrey"} icon={item.Agency_Director ? faCheckCircle : faCircle} size="sm" />
                              </Typography>
                            </div>
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Col>
              ))}
              {data.length === 0 && (
                <Col className="text-center mt-5">
                  <Typography color="text.secondary">
                    No certifications found.
                  </Typography>
                </Col>
              )}
            </Row>
          )}

          <Dialog
            open={Boolean(approvalRequest)}
            onClose={() => setApprovalRequest(null)}
            aria-labelledby="approve-certification-dialog-title"
            aria-describedby="approve-certification-dialog-description"
          >
            <DialogTitle id="approve-certification-dialog-title">
              Approve Certification
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="approve-certification-dialog-description">
                Approving this certification will initiate the ASO's process of creating the certificate package. Do you want to continue?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApprovalRequest(null)}>Cancel</Button>
              <Button onClick={handleApprovalConfirm} variant="contained" autoFocus>
                Yes, Start Preparation
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Fragment>
    </ErrorBoundary>
  );
}

export default AllCertificationsList;
